import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Banknote, ArrowRight, AlertCircle, CheckCircle, Building } from 'lucide-react';
import { bankAPI, walletAPI } from '../services/api';
import { formatCurrency } from '../utils/constants';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BankTransfer = () => {
  const [aedBalance, setAedBalance] = useState(0);
  const [uaeBanks, setUaeBanks] = useState([]);
  const [transferData, setTransferData] = useState({
    amount: '',
    bank_name: '',
    account_number: '',
    iban: '',
    account_holder_name: '',
    swift_code: ''
  });
  const [errors, setErrors] = useState({});
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [balanceRes, banksRes] = await Promise.all([
        walletAPI.getBalances(),
        bankAPI.getUAEBanks()
      ]);
      const aed = balanceRes.data.find(b => b.crypto === 'AED');
      setAedBalance(aed?.balance || 0);
      setUaeBanks(banksRes.data.banks || []);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  // Validation functions
  const validateIBAN = (iban) => {
    if (!iban) return { valid: false, message: 'IBAN is required' };
    iban = iban.replace(/\s/g, '').toUpperCase();
    if (iban.length !== 23) return { valid: false, message: 'UAE IBAN must be 23 characters' };
    if (!iban.startsWith('AE')) return { valid: false, message: 'UAE IBAN must start with "AE"' };
    if (!/^AE\d{2}\d{3}\d{16}$/.test(iban)) return { valid: false, message: 'Invalid IBAN format' };
    return { valid: true, message: '' };
  };

  const validateAccountNumber = (accountNumber) => {
    if (!accountNumber) return { valid: false, message: 'Account number is required' };
    accountNumber = accountNumber.replace(/\s/g, '');
    if (!/^\d+$/.test(accountNumber)) return { valid: false, message: 'Account number must contain only digits' };
    if (accountNumber.length < 10 || accountNumber.length > 16) {
      return { valid: false, message: 'Account number must be 10-16 digits' };
    }
    return { valid: true, message: '' };
  };

  const validateAmount = (amount) => {
    const num = parseFloat(amount);
    if (!amount || isNaN(num)) return { valid: false, message: 'Amount is required' };
    if (num < 10) return { valid: false, message: 'Minimum transfer amount is 10 AED' };
    if (num > 100000) return { valid: false, message: 'Maximum transfer amount is 100,000 AED' };
    if (num > aedBalance) return { valid: false, message: 'Insufficient balance' };
    return { valid: true, message: '' };
  };

  const validateAccountHolderName = (name) => {
    if (!name || name.trim().length < 2) return { valid: false, message: 'Account holder name is required (min 2 characters)' };
    return { valid: true, message: '' };
  };

  const handleInputChange = (field, value) => {
    setTransferData({ ...transferData, [field]: value });
    
    // Real-time validation
    let validation;
    switch (field) {
      case 'iban':
        validation = validateIBAN(value);
        break;
      case 'account_number':
        validation = validateAccountNumber(value);
        break;
      case 'amount':
        validation = validateAmount(value);
        break;
      case 'account_holder_name':
        validation = validateAccountHolderName(value);
        break;
      default:
        validation = { valid: true, message: '' };
    }
    
    setErrors(prev => ({
      ...prev,
      [field]: validation.valid ? '' : validation.message
    }));
  };

  const formatIBAN = (value) => {
    // Format IBAN with spaces every 4 characters for readability
    const clean = value.replace(/\s/g, '').toUpperCase();
    const formatted = clean.match(/.{1,4}/g)?.join(' ') || clean;
    return formatted;
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const ibanValidation = validateIBAN(transferData.iban);
    const accountValidation = validateAccountNumber(transferData.account_number);
    const amountValidation = validateAmount(transferData.amount);
    const holderValidation = validateAccountHolderName(transferData.account_holder_name);
    
    if (!transferData.bank_name) {
      toast.error('Please select a bank');
      return;
    }
    
    if (!ibanValidation.valid || !accountValidation.valid || !amountValidation.valid || !holderValidation.valid) {
      setErrors({
        iban: ibanValidation.valid ? '' : ibanValidation.message,
        account_number: accountValidation.valid ? '' : accountValidation.message,
        amount: amountValidation.valid ? '' : amountValidation.message,
        account_holder_name: holderValidation.valid ? '' : holderValidation.message
      });
      toast.error('Please fix the validation errors');
      return;
    }

    setTransferring(true);

    try {
      const response = await bankAPI.transfer({
        ...transferData,
        iban: transferData.iban.replace(/\s/g, '').toUpperCase(),
        account_number: transferData.account_number.replace(/\s/g, ''),
        amount: parseFloat(transferData.amount)
      });
      toast.success(`Transfer of ${formatCurrency(parseFloat(transferData.amount))} initiated successfully!`);
      setTransferData({ amount: '', bank_name: '', account_number: '', iban: '', account_holder_name: '', swift_code: '' });
      setErrors({});
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const isFormValid = () => {
    return (
      transferData.amount &&
      transferData.bank_name &&
      transferData.account_number &&
      transferData.iban &&
      transferData.account_holder_name &&
      !errors.iban &&
      !errors.account_number &&
      !errors.amount &&
      !errors.account_holder_name
    );
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-primary font-bold text-white mb-2">BANK TRANSFER</h1>
          <p className="text-gray-400 font-secondary mb-8">Withdraw AED to your UAE bank account</p>

          {/* Balance Display */}
          <div className="glass-card p-6 rounded-2xl mb-8" data-testid="aed-balance-display">
            <span className="text-gray-400 font-secondary text-sm uppercase tracking-wider">Available AED Balance</span>
            <div className="text-4xl font-mono font-bold text-[#00F0FF] mt-2">{formatCurrency(aedBalance)}</div>
          </div>

          {/* Transfer Form */}
          <div className="glass-card p-8 rounded-2xl">
            <form onSubmit={handleTransfer} className="space-y-6">
              {/* Amount */}
              <div>
                <Label className="text-gray-300 mb-2 block">Amount (AED)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="10"
                  max="100000"
                  placeholder="0.00"
                  value={transferData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono text-2xl py-6 ${
                    errors.amount ? 'border-red-500' : ''
                  }`}
                  required
                  data-testid="bank-transfer-amount-input"
                />
                {errors.amount && (
                  <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.amount}</span>
                  </div>
                )}
                <p className="text-gray-500 text-xs mt-2">Min: 10 AED | Max: 100,000 AED per transaction</p>
              </div>

              {/* Bank Name */}
              <div>
                <Label className="text-gray-300 mb-2 block">Bank Name</Label>
                <Select 
                  value={transferData.bank_name} 
                  onValueChange={(value) => setTransferData({ ...transferData, bank_name: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="bank-name-select">
                    <SelectValue placeholder="Select your bank" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A051E] border-white/10 max-h-60">
                    {uaeBanks.map((bank) => (
                      <SelectItem key={bank} value={bank} className="text-white hover:bg-white/10">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-gray-400" />
                          {bank}
                        </div>
                      </SelectItem>
                    ))}
                    <SelectItem value="other" className="text-white hover:bg-white/10">
                      Other Bank
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Account Holder Name */}
              <div>
                <Label className="text-gray-300 mb-2 block">Account Holder Name</Label>
                <Input
                  type="text"
                  placeholder="Full name as per bank account"
                  value={transferData.account_holder_name}
                  onChange={(e) => handleInputChange('account_holder_name', e.target.value)}
                  className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 ${
                    errors.account_holder_name ? 'border-red-500' : ''
                  }`}
                  required
                  data-testid="account-holder-name-input"
                />
                {errors.account_holder_name && (
                  <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.account_holder_name}</span>
                  </div>
                )}
              </div>

              {/* Account Number */}
              <div>
                <Label className="text-gray-300 mb-2 block">Account Number</Label>
                <Input
                  type="text"
                  placeholder="1234567890"
                  value={transferData.account_number}
                  onChange={(e) => handleInputChange('account_number', e.target.value.replace(/[^0-9]/g, ''))}
                  className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono ${
                    errors.account_number ? 'border-red-500' : ''
                  }`}
                  maxLength={16}
                  required
                  data-testid="account-number-input"
                />
                {errors.account_number ? (
                  <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.account_number}</span>
                  </div>
                ) : transferData.account_number && !errors.account_number && (
                  <div className="flex items-center gap-2 mt-2 text-[#00FF94] text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Valid account number format</span>
                  </div>
                )}
              </div>

              {/* IBAN */}
              <div>
                <Label className="text-gray-300 mb-2 block">IBAN</Label>
                <Input
                  type="text"
                  placeholder="AE07 0331 2345 6789 0123 456"
                  value={formatIBAN(transferData.iban)}
                  onChange={(e) => handleInputChange('iban', e.target.value)}
                  className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono tracking-wider ${
                    errors.iban ? 'border-red-500' : ''
                  }`}
                  maxLength={28}
                  required
                  data-testid="iban-input"
                />
                {errors.iban ? (
                  <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.iban}</span>
                  </div>
                ) : transferData.iban && !errors.iban && validateIBAN(transferData.iban).valid && (
                  <div className="flex items-center gap-2 mt-2 text-[#00FF94] text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Valid UAE IBAN format</span>
                  </div>
                )}
                <p className="text-gray-500 text-xs mt-2">UAE IBAN format: AE + 2 check digits + 3-digit bank code + 16-digit account</p>
              </div>

              {/* SWIFT Code (Optional) */}
              <div>
                <Label className="text-gray-300 mb-2 block">SWIFT/BIC Code (Optional)</Label>
                <Input
                  type="text"
                  placeholder="EMIRAEADXXX"
                  value={transferData.swift_code}
                  onChange={(e) => setTransferData({ ...transferData, swift_code: e.target.value.toUpperCase() })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono"
                  maxLength={11}
                  data-testid="swift-code-input"
                />
                <p className="text-gray-500 text-xs mt-2">8 or 11 characters (optional for domestic transfers)</p>
              </div>

              {/* Summary */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Transfer Fee</span>
                  <span className="text-[#00FF94] font-mono">Free</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Processing Time</span>
                  <span className="text-white">1-2 business days</span>
                </div>
                {transferData.amount && !errors.amount && (
                  <div className="flex items-center justify-between text-sm border-t border-white/10 pt-3">
                    <span className="text-gray-400">You will receive</span>
                    <span className="text-[#00F0FF] font-mono font-bold">{formatCurrency(parseFloat(transferData.amount) || 0)}</span>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-[#7000FF]/10 border border-[#7000FF]/30 rounded-xl p-4 text-sm">
                <p className="text-[#7000FF] font-semibold mb-1">Important Notes:</p>
                <ul className="text-gray-400 space-y-1">
                  <li>• Ensure bank details match exactly with your bank records</li>
                  <li>• Transfers to UAE banks only (IBAN starting with AE)</li>
                  <li>• Name mismatch may cause transfer rejection</li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={transferring || !isFormValid()}
                className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6 text-lg font-bold neon-glow-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-bank-transfer-btn"
              >
                {transferring ? 'Processing...' : (
                  <>
                    Transfer to Bank <ArrowRight className="ml-2 inline" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default BankTransfer;
